// 主订单状态双轨体系（扩展版）
export type MainOrderStatus =
  | "待支付"
  | "待商家确认"
  | "待签约"
  | "待出行"
  | "部分履约"
  | "全部履约"
  | "已完成"
  | "部分取消"
  | "全额取消"
  | "售后中"
  | "已取消"
  | "退款中";

export type HotelSubStatus = "待酒店确认" | "已确认·待入住" | "已入住" | "已离店" | "已完成" | "已取消" | "售后中";
// 注意：租车状态已移除"已派车"，直接从"待派车"→"待取车"→"使用中"→"已还车"
export type CarSubStatus = "待派车" | "待取车" | "使用中" | "已还车" | "已完成" | "已取消" | "售后中";
export type RouteSubStatus = "待成团" | "已成团·待出发" | "行程中" | "已完成" | "已完结" | "已取消" | "售后中";
export type GuideSubStatus = "待确认" | "已确认·待服务" | "服务中" | "已完成" | "已取消" | "售后中";

export interface CarInspectionPhoto {
  label: string;  // 车头/车身/车尾
  url: string;
}

export interface SubOrder {
  id: string;
  type: "hotel" | "car" | "tour" | "guide";
  icon: string;
  name: string;
  spec: string;
  dateRange: string;
  quantity: string;
  unitPrice: number;
  totalPrice: number;
  subStatus: string;
  subStatusColor: string;
  subStatusBg: string;
  subStatusNote?: string;
  detailPath: string;
  // 租车专属：验车照片（司机端上传）
  pickupPhotos?: CarInspectionPhoto[];   // 取车验车照片
  returnPhotos?: CarInspectionPhoto[];   // 还车验车照片
  // 租车人信息
  driverName?: string;
  driverIdCard?: string;
  driverLicense?: string;
}

export interface Order {
  id: string;
  orderNo?: string;
  type: "multi" | "single";
  mainStatus: MainOrderStatus;
  mainStatusStep: number;
  total: number;
  time: string;
  destination: string;
  travelers: number;
  subOrders?: SubOrder[];
  productType?: "hotel" | "car" | "tour" | "guide";
  productIcon?: string;
  productName?: string;
  productSpec?: string;
  productDate?: string;
  productSubStatus?: string;
  productSubStatusColor?: string;
  productSubStatusBg?: string;
}

export const mainStatusSteps = ["用户下单", "商家确认", "签订合同", "待出行", "已完成"];

export const statusStyle: Record<string, { color: string; bg: string; note?: string }> = {
  "待支付":     { color: "#D97706", bg: "#FEF3C7" },
  "待商家确认":  { color: "#6366F1", bg: "#EEF2FF" },
  "待签约":     { color: "#0EA5E9", bg: "#E0F2FE" },
  "待出行":     { color: "#2563EB", bg: "#DBEAFE", note: "所有服务待使用" },
  "部分履约":   { color: "#1E40AF", bg: "#DBEAFE" },
  "全部履约":   { color: "#1E3A8A", bg: "#DBEAFE", note: "所有服务已完成，待结算" },
  "已完成":     { color: "#059669", bg: "#D1FAE5", note: "订单已结算，可评价" },
  "部分取消":   { color: "#EA580C", bg: "#FFEDD5" },
  "全额取消":   { color: "#EA580C", bg: "#FFEDD5", note: "订单已全额取消，可查看详情" },
  "售后中":     { color: "#DC2626", bg: "#FEE2E2" },
  "已取消":     { color: "#9CA3AF", bg: "#F3F4F6" },
  "退款中":     { color: "#DC2626", bg: "#FEE2E2" },
};

export const orderList: Order[] = [
  // ── 全部完成的组合订单（用于展示整体售后+开票） ──
  {
    id: "M20250320002",
    type: "multi",
    mainStatus: "已完成",
    mainStatusStep: 4,
    total: 10160,
    time: "2025-03-01 09:00",
    destination: "三亚",
    travelers: 4,
    subOrders: [
      {
        id: "S002-H",
        type: "hotel",
        icon: "🏨",
        name: "三亚亚龙湾瑞吉度假酒店",
        spec: "豪华海景大床房",
        dateRange: "03-15 ~ 03-19",
        quantity: "1间4晚",
        unitPrice: 1280,
        totalPrice: 5120,
        subStatus: "已离店",
        subStatusColor: "#059669",
        subStatusBg: "#D1FAE5",
        subStatusNote: "03月15日 入住 · 03月19日 退房（共4晚）",
        detailPath: "/order/detail/hotel",
      },
      {
        id: "S002-C",
        type: "car",
        icon: "🚗",
        name: "别克GL8 陆尊版",
        spec: "7座商务车 · 含司机",
        dateRange: "03-15 09:00 ~ 03-19 18:00",
        quantity: "1辆4天",
        unitPrice: 225,
        totalPrice: 900,
        subStatus: "已还车",
        subStatusColor: "#374151",
        subStatusBg: "#F3F4F6",
        subStatusNote: "03月19日 18:00 已还车 · 三亚凤凰机场T2",
        detailPath: "/order/detail/car",
        driverName: "林晓雨",
        driverIdCard: "310***1234",
        driverLicense: "粤A***567",
        pickupPhotos: [
          { label: "车头", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20front%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph01&orientation=landscape" },
          { label: "车身", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20side%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph02&orientation=landscape" },
          { label: "车尾", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20rear%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph03&orientation=landscape" },
        ],
        returnPhotos: [
          { label: "车头", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20front%20view%20return%20inspection%20photo%20vehicle%20condition%20check%20clean&width=300&height=200&seq=carph04&orientation=landscape" },
          { label: "车身", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20side%20view%20return%20inspection%20photo%20vehicle%20condition%20check%20clean&width=300&height=200&seq=carph05&orientation=landscape" },
        ],
      },
      {
        id: "S002-R",
        type: "tour",
        name: "三亚5日经典深度游",
        spec: "含导游·含门票·含部分餐食",
        dateRange: "03-15 ~ 03-19",
        quantity: "4人",
        unitPrice: 600,
        totalPrice: 2400,
        subStatus: "已完成",
        subStatusColor: "#059669",
        subStatusBg: "#D1FAE5",
        subStatusNote: "行程已圆满完成",
        detailPath: "/order/detail/route",
      },
      {
        id: "S002-G",
        type: "guide",
        icon: "👩‍✈️",
        name: "导游 · 陈美玲",
        spec: "普通话/英语 · 全日服务",
        dateRange: "03-15 ~ 03-17（3天）",
        quantity: "3天",
        unitPrice: 580,
        totalPrice: 1740,
        subStatus: "已完成",
        subStatusColor: "#059669",
        subStatusBg: "#D1FAE5",
        subStatusNote: "服务已完成，评分 ⭐4.9",
        detailPath: "/order/detail/guide",
      },
    ],
  },
  {
    id: "M20250413001",
    type: "multi",
    mainStatus: "待出行",
    mainStatusStep: 3,
    total: 8000,
    time: "2025-04-10 14:23",
    destination: "三亚",
    travelers: 4,
    subOrders: [
      {
        id: "S001-H",
        type: "hotel",
        icon: "🏨",
        name: "三亚亚龙湾瑞吉度假酒店",
        spec: "豪华海景大床房",
        dateRange: "05-01 ~ 05-05",
        quantity: "1间4晚",
        unitPrice: 1280,
        totalPrice: 5120,
        subStatus: "已确认·待入住",
        subStatusColor: "#2563EB",
        subStatusBg: "#DBEAFE",
        subStatusNote: "05月01日 入住 · 05月05日 退房（共4晚）",
        detailPath: "/order/detail/hotel",
      },
      {
        id: "S001-C",
        type: "car",
        icon: "🚗",
        name: "别克GL8 陆尊版",
        spec: "7座商务车 · 含司机",
        dateRange: "05-01 14:00 ~ 05-05 12:00",
        quantity: "1辆4天",
        unitPrice: 225,
        totalPrice: 900,
        subStatus: "使用中",
        subStatusColor: "#059669",
        subStatusBg: "#D1FAE5",
        subStatusNote: "05月01日 14:00 取车 · 三亚凤凰机场T2",
        detailPath: "/order/detail/car",
        driverName: "林晓雨",
        driverIdCard: "310***1234",
        driverLicense: "粤A***567",
        pickupPhotos: [
          { label: "车头", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20front%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph01&orientation=landscape" },
          { label: "车身", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20side%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph02&orientation=landscape" },
          { label: "车尾", url: "https://readdy.ai/api/search-image?query=Buick%20GL8%20white%20MPV%20rear%20view%20parking%20lot%20inspection%20photo%20clean%20vehicle%20condition%20check&width=300&height=200&seq=carph03&orientation=landscape" },
        ],
      },
      {
        id: "S001-R",
        type: "tour",
        name: "三亚5日经典深度游",
        spec: "含导游·含门票·含部分餐食",
        dateRange: "05-01 ~ 05-05",
        quantity: "4人",
        unitPrice: 600,
        totalPrice: 2400,
        subStatus: "已成团·待出发",
        subStatusColor: "#059669",
        subStatusBg: "#D1FAE5",
        subStatusNote: "05月01日 08:30 集合 · 行前通知已发",
        detailPath: "/order/detail/route",
      },
      {
        id: "S001-G",
        type: "guide",
        icon: "👩‍✈️",
        name: "导游 · 陈美玲",
        spec: "普通话/英语 · 全日服务",
        dateRange: "05-01 ~ 05-03（3天）",
        quantity: "3天",
        unitPrice: 580,
        totalPrice: 1740,
        subStatus: "已确认·待服务",
        subStatusColor: "#7C3AED",
        subStatusBg: "#F5F3FF",
        subStatusNote: "05月01日开始服务，共3天",
        detailPath: "/order/detail/guide",
      },
    ],
  },
  {
    id: "S20250412001",
    type: "single",
    mainStatus: "待商家确认",
    mainStatusStep: 1,
    total: 688,
    time: "2025-04-12 09:15",
    destination: "成都",
    travelers: 2,
    productType: "hotel",
    productIcon: "🏨",
    productName: "成都宽窄巷子精品民宿",
    productSpec: "中式庭院大床房 · 含早",
    productDate: "2025-05-10 ~ 2025-05-11",
    productSubStatus: "待酒店确认",
    productSubStatusColor: "#6366F1",
    productSubStatusBg: "#EEF2FF",
  },
  {
    id: "S20250401002",
    type: "single",
    mainStatus: "已完成",
    mainStatusStep: 4,
    total: 1200,
    time: "2025-03-20 10:30",
    destination: "大理",
    travelers: 2,
    productType: "car",
    productIcon: "🚗",
    productName: "大理洱海环线租车 · 途观L",
    productSpec: "5座SUV · 不限里程",
    productDate: "2025-04-01 ~ 2025-04-03",
    productSubStatus: "已还车",
    productSubStatusColor: "#374151",
    productSubStatusBg: "#F3F4F6",
  },
  {
    id: "S20250408003",
    type: "single",
    mainStatus: "待签约",
    mainStatusStep: 2,
    total: 4800,
    time: "2025-04-08 16:42",
    destination: "三亚",
    travelers: 6,
    productType: "tour",
    productIcon: "🗺️",
    productName: "三亚5日亲子深度游",
    productSpec: "含酒店·含导游·含门票",
    productDate: "2025-05-20 ~ 2025-05-25",
    productSubStatus: "待成团",
    productSubStatusColor: "#059669",
    productSubStatusBg: "#D1FAE5",
  },
];
